import {
    GetEarlyRepaymentSimulationResponse,
    GetPaymentMethodResponse
} from '@iwoca/lapi-client/edge';
import { mergeStates } from '@iwProvider/StoreHelper';
import { v4 as uuidv4 } from 'uuid';

import {
    CONFIRM_EARLY_REPAYMENT_LOADING,
    EARLY_REPAYMENT_ACCOUNT_ID,
    EARLY_REPAYMENT_CHOOSE_REPAYMENT_AMOUNT_DATA,
    EARLY_REPAYMENT_CONFIRM_REPAYMENT_DATA,
    EARLY_REPAYMENT_LOADING,
    EARLY_REPAYMENT_READY,
    LEAVE_EARLY_REPAYMENT,
    REQUEST_NEW_REPAYMENT_SCHEDULE_TO_REPAY_LOADING,
    SELECT_REPAYMENT_METHOD,
    SHOW_CHOOSE_REPAYMENT_AMOUNT_STEP,
    SHOW_CONFIRM_REPAYMENT_STEP,
    SHOW_TRUELAYER_CONFIRMATION,
    UPDATE_CONFIRM_REPAYMENT_SCHEDULE_EARLY_REPAYMENT,
    UPDATE_REPAYMENT_SCHEDULE_EARLY_REPAYMENT,
    UPDATE_SELECTED_REPAYMENT_AMOUNT
} from './EarlyRepayment.actions';
import {
    calculateDisableRepaymentMethodSelection,
    calculateFollowingRepaymentAmount,
    calculateFollowingRepaymentStatus,
    calculateFurtherMonthlyRepayments,
    calculateNumberOfNextRepayments,
    calculateOutstandingBalance,
    calculateReducedNextRepayment,
    calculateReducedNextRepaymentAmount,
    getEqualRepaymentAmount,
    getEqualRepaymentDate,
    getEqualRepaymentInterval,
    getNextRepaymentAmount,
    getNextRepaymentDate,
    getNextRepaymentStatus,
    getPaymentMethodOptions,
    getPreviousScheduleAmount,
    hasFollowingRepaymentsLeft,
    hasSameEqualRepaymentAmount
} from './EarlyRepaymentHelper';

const initialState = {
    ready: false,
    step: '',
    loadingNewRepaymentSchedule: null,
    selectedInputAmount: null,
    selectedSliderAmount: null,
    outstandingBalance: null,
    remainingOutstandingBalance: null,
    accountId: null,
    error: null,
    nextRepaymentStatus: null,
    nextRepaymentAmount: null,
    nextRepaymentDate: null,
    furtherRepaymentAmount: null,
    previousNextRepaymentAmount: null,
    numberOfNextRepayments: null,
    reducedNextRepayment: null,
    reducedNextRepaymentAmount: null,
    followingRepaymentStatus: null,
    followingRepaymentAmount: null,
    waitingConfirmation: null,
    monthlyRepaymentsCounter: null,
    furtherRepaymentDate: null,
    previousFurtherRepayments: null,
    furtherRepaymentInterval: null,
    hasSameEqualRepaymentAmount: null,
    disabledRepaymentMethodSelection: false,
    paymentOptions: [],
    earlyRepaymentIdempotencyKey: null,
    selectedPayment: null
};

export type TState = {
    ready: boolean;
    step: string;
    loadingNewRepaymentSchedule: boolean | null;
    selectedInputAmount: number | null;
    selectedSliderAmount: number | null;
    outstandingBalance: number | null;
    remainingOutstandingBalance: number | null;
    accountId: string | null;
    error: null;
    nextRepaymentStatus:
        'NORMAL' | 'PARTIAL_FUTURE' | 'PARTIAL_TODAY' | 'EXACT_AMOUNT' | null;
    nextRepaymentAmount: number | null;
    nextRepaymentDate: string | null;
    furtherRepaymentAmount: string | null;
    previousNextRepaymentAmount: number | null;
    numberOfNextRepayments: number | null;
    reducedNextRepayment: number | null;
    reducedNextRepaymentAmount: number | null;
    followingRepaymentStatus: 'EQUAL' | 'FULL_REPAYMENT' | null;
    followingRepaymentAmount: number | null;
    waitingConfirmation: boolean | null;
    monthlyRepaymentsCounter: number | null;
    furtherRepaymentDate: string | null;
    previousFurtherRepayments: number | null;
    furtherRepaymentInterval: string | null;
    hasSameEqualRepaymentAmount: boolean | null;
    disabledRepaymentMethodSelection: boolean;
    paymentOptions:
        | {
              type: string;
              value: string;
              cardLastDigits: string;
              issuer: string;
              brand: string;
              label: string;
          }[]
        | [];
    earlyRepaymentIdempotencyKey: null;
    selectedPayment: TSelectedPayment | null;
};

export type TSelectedPayment = {
    type: string;
    value: string;
    cardLastDigits: string;
    issuer: string;
    brand: string;
    label: string;
};

type TAction =
    | { type: typeof EARLY_REPAYMENT_LOADING }
    | { type: typeof EARLY_REPAYMENT_READY }
    | { type: typeof LEAVE_EARLY_REPAYMENT }
    | { type: typeof SHOW_CHOOSE_REPAYMENT_AMOUNT_STEP }
    | { type: typeof SHOW_CONFIRM_REPAYMENT_STEP }
    | { type: typeof EARLY_REPAYMENT_ACCOUNT_ID; accountId: string }
    | {
          type: typeof EARLY_REPAYMENT_CHOOSE_REPAYMENT_AMOUNT_DATA;
          inputAmount: number;

          outstandingBalance: number;
          initialRemainingOustandingBalance: number;
          accountId: string;
      }
    | { type: typeof REQUEST_NEW_REPAYMENT_SCHEDULE_TO_REPAY_LOADING }
    | {
          type: typeof UPDATE_REPAYMENT_SCHEDULE_EARLY_REPAYMENT;
          current: { response: GetEarlyRepaymentSimulationResponse };
          initial: { response: GetEarlyRepaymentSimulationResponse };
      }
    | { type: typeof EARLY_REPAYMENT_CONFIRM_REPAYMENT_DATA }
    | {
          type: typeof UPDATE_SELECTED_REPAYMENT_AMOUNT;
          outstandingBalance: number;
          inputAmount: number;
          sliderAmount: number;
      }
    | {
          type: typeof UPDATE_CONFIRM_REPAYMENT_SCHEDULE_EARLY_REPAYMENT;
          current: { response: GetEarlyRepaymentSimulationResponse };
          initial: { response: GetEarlyRepaymentSimulationResponse };
          payment_method: GetPaymentMethodResponse['data'];
      }
    | {
          type: typeof SELECT_REPAYMENT_METHOD;
          selectedPayment: TSelectedPayment;
      }
    | { type: typeof CONFIRM_EARLY_REPAYMENT_LOADING }
    | { type: typeof SHOW_TRUELAYER_CONFIRMATION };

// eslint-disable-next-line import/no-default-export
export default function (
    state: TState = mergeStates(initialState),
    action: TAction
): TState {
    switch (action.type) {
        case EARLY_REPAYMENT_LOADING:
            return mergeStates(state, { ready: false });

        case EARLY_REPAYMENT_READY:
            return mergeStates(state, { ready: true });

        case LEAVE_EARLY_REPAYMENT:
            return mergeStates(initialState);

        case SHOW_CHOOSE_REPAYMENT_AMOUNT_STEP:
            return mergeStates(state, {
                ready: false,
                step: 'CHOOSE_REPAYMENT_AMOUNT',
                selectedInputAmount: 0,
                selectedSliderAmount: 0,
                outstandingBalance: 0,
                loadingNewRepaymentSchedule: true
            });

        case SHOW_CONFIRM_REPAYMENT_STEP:
            return mergeStates(state, {
                ready: false,
                step: 'CONFIRM_REPAYMENT'
            });

        case EARLY_REPAYMENT_ACCOUNT_ID:
            return mergeStates(state, {
                accountId: action.accountId
            });

        case EARLY_REPAYMENT_CHOOSE_REPAYMENT_AMOUNT_DATA:
            return mergeStates(state, {
                ready: true,
                loadingNewRepaymentSchedule: true,
                step: 'CHOOSE_REPAYMENT_AMOUNT',
                selectedInputAmount: action.inputAmount,
                selectedSliderAmount: action.inputAmount,
                outstandingBalance: action.outstandingBalance,
                remainingOutstandingBalance:
                    action.initialRemainingOustandingBalance,
                accountId: action.accountId,
                error: null,
                nextRepaymentStatus: null,
                nextRepaymentAmount: null,
                numberOfNextRepayments: null,
                reducedNextRepaymentAmount: null,
                followingRepaymentStatus: null,
                followingRepaymentAmount: null
            });

        case REQUEST_NEW_REPAYMENT_SCHEDULE_TO_REPAY_LOADING:
            return mergeStates(state, {
                loadingNewRepaymentSchedule: true
            });

        case UPDATE_REPAYMENT_SCHEDULE_EARLY_REPAYMENT:
            return mergeStates(state, {
                loadingNewRepaymentSchedule: false,
                error: false,
                nextRepaymentStatus: getNextRepaymentStatus(action),
                hasFollowingRepaymentsLeft: hasFollowingRepaymentsLeft(
                    action.current
                ),
                numberOfNextRepayments: calculateNumberOfNextRepayments(action),
                reducedNextRepayment: calculateReducedNextRepayment(action),
                reducedNextRepaymentAmount:
                    calculateReducedNextRepaymentAmount(action),
                followingRepaymentStatus:
                    calculateFollowingRepaymentStatus(action),
                followingRepaymentAmount:
                    calculateFollowingRepaymentAmount(action)
            });

        case EARLY_REPAYMENT_CONFIRM_REPAYMENT_DATA:
            return mergeStates(state, {
                ready: true,
                step: 'CONFIRM_REPAYMENT'
            });

        case UPDATE_SELECTED_REPAYMENT_AMOUNT:
            return mergeStates(state, {
                loadingNewRepaymentSchedule: true,
                selectedInputAmount: action.inputAmount,
                selectedSliderAmount: action.sliderAmount,
                remainingOutstandingBalance: calculateOutstandingBalance(
                    state,
                    action
                ),
                earlyRepaymentIdempotencyKey: uuidv4().toString()
            });

        case UPDATE_CONFIRM_REPAYMENT_SCHEDULE_EARLY_REPAYMENT:
            return mergeStates(state, {
                loadingNewRepaymentSchedule: false,
                nextRepaymentStatus: getNextRepaymentStatus(action),
                nextRepaymentAmount: getNextRepaymentAmount(action.current),
                nextRepaymentDate: getNextRepaymentDate(action.current),
                furtherRepaymentAmount: getEqualRepaymentAmount(action.current),
                furtherRepaymentDate: getEqualRepaymentDate(action.current),
                furtherRepaymentInterval: getEqualRepaymentInterval(
                    action.current
                ),
                monthlyRepaymentsCounter: calculateFurtherMonthlyRepayments(
                    action.current
                ),
                previousNextRepaymentAmount: getNextRepaymentAmount(
                    action.initial
                ),
                previousFurtherRepayments: getPreviousScheduleAmount(
                    action.initial
                ),
                hasSameEqualRepaymentAmount:
                    hasSameEqualRepaymentAmount(action),
                numberOfNextRepayments: calculateNumberOfNextRepayments(action),
                reducedNextRepayment: calculateReducedNextRepayment(action),
                reducedNextRepaymentAmount:
                    calculateReducedNextRepaymentAmount(action),
                followingRepaymentStatus:
                    calculateFollowingRepaymentStatus(action),
                followingRepaymentAmount:
                    calculateFollowingRepaymentAmount(action),
                disabledRepaymentMethodSelection:
                    calculateDisableRepaymentMethodSelection(),
                ...getPaymentMethodOptions(action)
            });

        case SELECT_REPAYMENT_METHOD:
            return mergeStates(state, {
                ...state,
                selectedPayment: action.selectedPayment
            });

        case CONFIRM_EARLY_REPAYMENT_LOADING:
            return mergeStates(state, {
                waitingConfirmation: true
            });

        case SHOW_TRUELAYER_CONFIRMATION:
            return mergeStates(state, {
                ready: true,
                step: 'SHOW_TRUELAYER_CONFIRMATION'
            });

        default:
            return state;
    }
}
